'use client'

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { AnyPageState, PageStateAction } from './types'

type PageStateContextValue = {
  /** Current page snapshot, or null if no page has registered yet */
  state: AnyPageState | null
  /** Register/update the current page snapshot. Called from the page component. */
  setState: (next: AnyPageState | null) => void
  /** Subscribe to action dispatches. Returns an unsubscribe function. */
  subscribe: (listener: (action: PageStateAction) => void) => () => void
  /** Dispatch an action to all subscribers. Called by chat tool handlers. */
  dispatch: (action: PageStateAction) => void
}

const PageStateContext = createContext<PageStateContextValue | null>(null)

/**
 * Module-level pending action buffer.
 *
 * If the chat dispatches an action *before* a page has registered its
 * listener (e.g. immediately after navigation), the action would be lost.
 * Instead, we queue actions here when there are no listeners, and flush the
 * queue as soon as the first listener subscribes.
 *
 * This lives at module scope (not in a ref) so it survives the navigation
 * remount of the lobby layout's children.
 */
const pendingActionQueue: PageStateAction[] = []

export function PageStateProvider({ children }: { children: React.ReactNode }) {
  const [state, setStateInternal] = useState<AnyPageState | null>(null)
  const listenersRef = useRef<Set<(action: PageStateAction) => void>>(new Set())

  const setState = useCallback((next: AnyPageState | null) => {
    setStateInternal(next)
    // Mirror current snapshot onto window so non-React consumers (and the
    // chat tool handlers in other modules) can read it synchronously.
    if (typeof window !== 'undefined') {
      ;(window as any).__atriumPageState = next
    }
  }, [])

  const subscribe = useCallback((listener: (action: PageStateAction) => void) => {
    listenersRef.current.add(listener)
    // Flush any actions that were queued before the listener subscribed.
    // Use a microtask so the listener is fully wired before we replay.
    if (pendingActionQueue.length > 0) {
      Promise.resolve().then(() => {
        const queued = pendingActionQueue.splice(0, pendingActionQueue.length)
        for (const action of queued) {
          try {
            listener(action)
          } catch (err) {
            console.error('[page-state] replay listener error:', err)
          }
        }
      })
    }
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const dispatch = useCallback((action: PageStateAction) => {
    if (listenersRef.current.size === 0) {
      // No page is listening yet — buffer the action and replay it as soon as
      // a page subscribes. Cap the queue so we don't grow unbounded if the
      // user navigates away.
      if (pendingActionQueue.length < 10) {
        pendingActionQueue.push(action)
      }
      console.log('[page-state] dispatched with no listeners — queued:', action)
      return
    }
    listenersRef.current.forEach((listener) => {
      try {
        listener(action)
      } catch (err) {
        console.error('[page-state] listener error:', err)
      }
    })
  }, [])

  // Expose the dispatch function on window so chat tool handlers can call it
  // from anywhere without having to thread context through.
  useEffect(() => {
    if (typeof window === 'undefined') return
    ;(window as any).__atriumDispatchPageAction = dispatch
    return () => {
      if ((window as any).__atriumDispatchPageAction === dispatch) {
        delete (window as any).__atriumDispatchPageAction
      }
    }
  }, [dispatch])

  return (
    <PageStateContext.Provider value={{ state, setState, subscribe, dispatch }}>
      {children}
    </PageStateContext.Provider>
  )
}

export function usePageStateContext() {
  const ctx = useContext(PageStateContext)
  if (!ctx) {
    throw new Error('usePageStateContext must be used within a PageStateProvider')
  }
  return ctx
}

/**
 * Hook for pages to publish their current state snapshot + subscribe to
 * action dispatches. Call this from a page-level client component.
 *
 * @param snapshot  The current page state to publish. Pass null to clear.
 * @param onAction  Handler for dispatched actions. The page decides what
 *                  to do with each action type.
 */
export function usePageStateSync(
  snapshot: AnyPageState | null,
  onAction?: (action: PageStateAction) => void
) {
  const { setState, subscribe } = usePageStateContext()

  // Publish snapshot whenever it changes (reference identity)
  useEffect(() => {
    setState(snapshot)
    return () => {
      // Clear on unmount so the chat doesn't think a stale page is still open
      setState(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot])

  // Subscribe to actions
  const onActionRef = useRef(onAction)
  useEffect(() => {
    onActionRef.current = onAction
  }, [onAction])

  useEffect(() => {
    if (!onAction) return
    const unsubscribe = subscribe((action) => {
      onActionRef.current?.(action)
    })
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe])
}

/**
 * Server-side / non-React read of the current page snapshot.
 * Used by chat tool handlers.
 */
export function getPageStateSnapshot(): AnyPageState | null {
  if (typeof window === 'undefined') return null
  return ((window as any).__atriumPageState as AnyPageState) ?? null
}

/**
 * Non-React dispatch into the page state bus.
 * Used by chat tool handlers that run in the browser.
 */
export function dispatchPageAction(action: PageStateAction): boolean {
  if (typeof window === 'undefined') return false
  const fn = (window as any).__atriumDispatchPageAction as
    | ((action: PageStateAction) => void)
    | undefined
  if (!fn) return false
  fn(action)
  return true
}
